import m, {Vnode} from "mithril";
import Chatbot from "../Chatbot";
import TMessage from "../model/TMessage";
import decodeHTMLEntities from "../../../../helpers/decodeHtmlEntities";
import htmlSubstring from "../../../../helpers/htmlSubstring";
import TAttachment from "../model/TAttachment";
import PopoversExtension from "../../../../extensions/PopoversExtension";
import TableExportExtension from "../../../../extensions/TableExportExtension";
import CitationPreview from "../CitationPreview";
import {Popover} from "bootstrap";

export default class Messages {

    private readonly popoverExtension;
    private readonly tableExportExtension;
    private readonly previews = new Map<Element, {content: string, root: HTMLElement, preview: CitationPreview}>();

    constructor(
        private readonly chatbot: Chatbot
    ) {
        this.popoverExtension = new PopoversExtension();
        this.tableExportExtension = new TableExportExtension();
    }

    private assistantMessage(message: TMessage, animate: boolean): Vnode {
        return m('.chatbot-message.d-flex.flex-row.flex-nowrap.gap-2.mb-3'+(animate?'.chatbot-animate-message':''), {}, [
            m('.chatbot-avatar.avatar', {
                title: 'Tina',
            }),
            m('.message.d-flex.flex-column.flex-grow-1.align-items-start.gap-1.p-2.position-relative', {
                oncreate: vnode => this.upgradeMessage(message, vnode.dom),
                onbeforeupdate: (vnode, old) => this.prepareMessageUpdate(message, old.dom),
                onupdate: vnode => this.upgradeMessage(message, vnode.dom),
                onremove: vnode => this.removeMessage(vnode.dom),
            }, [
                m(".message-content.w-100", {}, this.assistantMessageContent(message)),
                message.completed ? this.copyButton() : null,
                (message.completed && message.attachments?.length) ? this.viewMessageAttachments(message.attachments) : null,
            ]),
        ]);
    }

    private upgradeMessage(message: TMessage, element: Element): void {
        const current = this.previews.get(element);
        const completed = message.completed || message.role === 'user';
        if (current?.content === message.content && completed) {
            return;
        }
        this.removeMessage(element);
        if (completed) {
            const content = element.querySelector<HTMLElement>('.message-content');
            this.previews.set(element, {
                content: message.content,
                root: content,
                preview: new CitationPreview(content, this.chatbot.context),
            });
            this.popoverExtension.init($(content));
            if (message.role === 'assistant') {
                this.tableExportExtension.init($(content));
            }
        }
    }

    private prepareMessageUpdate(message: TMessage, element: Element): void {
        const current = this.previews.get(element);
        if (current && (current.content !== message.content || (!message.completed && message.role !== 'user'))) {
            this.removeMessage(element);
        }
    }

    private removeMessage(element: Element): void {
        const current = this.previews.get(element);
        current?.preview.destroy();
        this.previews.delete(element);
        (current?.root || element).querySelectorAll<HTMLElement>('[data-bs-toggle="popover"]').forEach(badge => {
            Popover.getInstance(badge)?.dispose();
            $(badge).removeData('popover-initialized');
        });
    }

    private assistantMessageContent(message: TMessage): Vnode {
        if (message.content === '...') {
            return m('i.opacity-50', {}, this.chatbot.translator.translate('thinking'));
        }

        return m.trust(message.content);
    }

    private viewMessageAttachments(attachments: TAttachment[]): Vnode {
        const images = attachments.filter(a => a.type === 'image');
        const others = attachments.filter(a => a.type !== 'image');

        return m('.message-attachments.w-100.mt-1.border-top', {}, [
            images.length ? m('.images.d-flex.flex-wrap.gap-2.mt-2', {},
                images.map(a => m('img.cursor-pointer.border', {
                    alt: a.originalFilename,
                    title: a.originalFilename,
                    src: this.chatbot.api.getDownloadFileUrl(a.uuid, true),
                    onclick: () => this.chatbot.state.imageDetailUrl = this.chatbot.api.getDownloadFileUrl(a.uuid, false),
                })),
            ) : null,
            others.length ? m('.p-2.others', {},
                others.map(a => {
                    const extension = a.fileName.toLowerCase().split('.').pop();
                    const icon = {
                        pdf: 'file-earmark-pdf',
                        doc: 'file-earmark-word',
                        docx: 'file-earmark-word',
                        xls: 'file-earmark-excel',
                        xlsx: 'file-earmark-excel',
                    }[extension] ?? 'file-earmark-text';

                    return m('.others.d-flex', {}, [
                        m(`i.bi.bi-${icon}.py-1.ps-0.pe-1`),
                        m('a.p-1', {
                            href: this.chatbot.api.getDownloadFileUrl(a.uuid, false),
                            download: true,
                        }, a.originalFilename),
                    ]);
                })
            ) : null,
        ]);
    }

    private userMessage(message: TMessage, animate: boolean): Vnode {
        return m('.user-message.d-flex.flex-row-reverse.flex-nowrap.align-self-end.gap-2.mb-3'+(animate?'.chatbot-animate-message':''), {}, [
            m('img.avatar.d-none.d-md-block', {
                src: this.chatbot.state.avatarPath,
                title: this.chatbot.state.nick,
                alt: this.chatbot.translator.translate('avatar'),
            }),
            m('.message.d-flex.flex-column.flex-grow-1.gap-1.p-2', {
                oncreate: vnode => this.upgradeMessage(message, vnode.dom),
                onbeforeupdate: (vnode, old) => this.prepareMessageUpdate(message, old.dom),
                onupdate: vnode => this.upgradeMessage(message, vnode.dom),
                onremove: vnode => this.removeMessage(vnode.dom),
            }, [
                m('.message-content', m.trust(message.content)),
                (message.completed && message.attachments?.length) ? this.viewMessageAttachments(message.attachments) : null,
            ])
        ]);
    }

    private viewErrorMessage(): Vnode {
        return m('.errorMessage', {}, [
            m('img.mb-4', {
                src: '/assets/images/chatbot/tina-error.png',
            }),
            m('.', {}, m.trust(this.chatbot.translator.translate('error_general'))),
            m('a.btn.btn-primary.mt-3', {
                href: this.chatbot.state.newConversationUrl,
            }, this.chatbot.translator.translate('start_new_conversation')),
        ]);
    }

    private copyButton(): Vnode {
        return m('button.btn.btn-sm.btn-flat-primary', {
            title: this.chatbot.translator.translate('copy'),
            onclick: (e: MouseEvent) => {
                e.preventDefault();

                const content = (e.currentTarget as HTMLElement).closest('.message')
                    .querySelector('.message-content').cloneNode(true) as HTMLElement;
                content.querySelectorAll('[data-table-export], .popover, template[data-translation-original]')
                    .forEach(element => element.remove());
                content.querySelectorAll('*').forEach(element => {
                    Array.from(element.attributes).forEach(attribute => {
                        if (attribute.name.startsWith('data-citation-') || attribute.name.startsWith('data-bs-')
                            || attribute.name.startsWith('data-translation-') || attribute.name.startsWith('data-content-translation')
                            || ['data-component', 'data-component-initialized', 'data-options', 'aria-describedby'].includes(attribute.name)) {
                            element.removeAttribute(attribute.name);
                        }
                    });
                });
                const html = content.innerHTML;
                const plain = decodeHTMLEntities(html.replaceAll('\n', '')
                    .replaceAll('<li>', '- ')
                    .replace(/<\/p><\/h3>|<\/h3>|<\/p><\/li>|<\/p>|<\/li>|<\/ol>/ig, '\n')
                    .replace(/(<([^>]+)>)/ig, ''));

                const clipboardItem = new ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([plain], { type: 'text/plain' })
                });

                navigator.clipboard.write([clipboardItem])
                    .then(() => console.log("clipboard.write() Ok"))
                    .catch(error => alert(error))

            },
        }, m('i.bi.bi-copy'));
    }

    private chatbotThink(): Vnode {
        return m('.chatbot-think', {}, Array(3).fill(m('span', '.')));
    }

    /**
     * Get message with animated substring and keep animation go brrrr
     * When animation should not run, it returns null
     */
    private getAnimatedMessage(): TMessage|null {
        if (this.chatbot.state.animateFirstMessageText) {
            const length = Math.round((Date.now() - this.chatbot.state.started) / 20); // 1 symbol every n ms
            const firstMessage = this.chatbot.state.messages.find(msg => msg.role === 'assistant');

            if (!firstMessage || length > firstMessage.content.length) {
                this.chatbot.state.animateFirstMessageText = false;
                return null;
            }

            setTimeout(() => m.redraw(), 32);

            return {
                ...firstMessage,
                content: htmlSubstring(firstMessage.content, length),
                completed: false,
            };
        }

        return null;
    }

    private viewMessages(messages: TMessage[]): Vnode[] {
        return messages.map((message,i) => {
            if (message.role === 'assistant') {
                return this.assistantMessage(message, this.chatbot.state.animateMessages && i>0);
            }
            return this.userMessage(message, this.chatbot.state.animateMessages && i>0)
        })
    }

    view() {
        const animatedMessage = this.getAnimatedMessage();
        if (animatedMessage) {
            const firstAssistantMessageIndex = this.chatbot.state.messages.findIndex(msg => msg.role === 'assistant');
            const messagesUntilAnimated = this.chatbot.state.messages.slice(0, firstAssistantMessageIndex);

            return m('.chatbot-scroll-wrapper.w-100.d-flex.flex-column.flex-grow-1',{}, [
                m(`.chatbot-conversation-wrapper.d-flex.flex-column.flex-grow-1.w-100`, {}, [
                    ...this.viewMessages(messagesUntilAnimated),
                    this.assistantMessage(animatedMessage, false),
                ]),
            ]);
        }

        return m('.chatbot-scroll-wrapper.w-100.d-flex.flex-column.flex-grow-1', {
            onupdate: vnode => {
                if (this.chatbot.state.autoScroll) {
                    vnode.dom.scrollTop = vnode.dom.scrollHeight;
                }
            },
            onscroll: e => {
                this.chatbot.state.autoScroll = e.currentTarget.scrollTop >= e.currentTarget.scrollHeight-e.currentTarget.clientHeight;
            },
        }, [
            m('.chatbot-conversation-wrapper.d-flex.flex-column.flex-grow-1.w-100', {}, [
                ...this.viewMessages(this.chatbot.state.messages.filter(message =>
                    !this.chatbot.state.recovering || message.role !== 'assistant' || message.content !== '...'
                )),
                this.chatbot.state.sending && !this.chatbot.state.recovering ? this.chatbotThink() : null,
                this.chatbot.state.hasError ? this.viewErrorMessage() : null,
                this.chatbot.state.recovering && !this.chatbot.state.hasError
                    ? m('.p-3', {role: 'status'}, this.chatbot.translator.translate('connection_recovering')) : null,
            ]),
        ]);
    }
}
